import type { Apunte } from '../api/accounting';
import type { Comision } from '../api/commissions';
import { importeApunte } from './importe';

// Los datos del libro del año, sin formato: de aquí salen el CSV y el Excel, así
// que los dos cuentan siempre lo mismo.

// Una comisión es una fila de ingreso más en el libro (categoría "Comisiones").
// Su fecha sale del created_at (unix en string); el importe es el total.
function comisionComoApunte(c: Comision): Apunte {
  const fecha = new Date(Number(c.created_at) * 1000).toISOString().slice(0, 10);
  const [y, m] = fecha.split('-').map(Number);
  return {
    doc_id: c.commission_id,
    tipo: 'ingreso',
    categoria: 'Comisiones',
    concepto: c.concepto || 'Comisión',
    contraparte: '',
    fecha,
    trimestre: `${y}-T${Math.floor((m - 1) / 3) + 1}`,
    base_imponible: c.importe,
    iva_pct: 0,
    iva_amount: 0,
    total: c.importe,
    estado: 'confirmed',
  };
}

export interface Trimestre {
  t: 'T1' | 'T2' | 'T3' | 'T4' | 'TOTAL AÑO';
  ingresos: number;
  gastos: number;
  /** Lo retenido de IRPF a proveedores: la cifra del Modelo 111. */
  retenido: number;
}

export interface Libro {
  /** Solo lo confirmado, más las comisiones del año. Ingresos primero, por fecha. */
  filas: Apunte[];
  /** T1…T4 y el total del año. Ingresos y gastos, con el importe de verdad
   *  (base + IVA: en una factura con retención, el total más lo retenido). */
  trimestres: Trimestre[];
  hayRetenciones: boolean;
}

export function datosLibro(anio: string, apuntes: Apunte[], comisiones: Comision[]): Libro {
  const filas: Apunte[] = [
    ...apuntes.filter((a) => a.estado === 'confirmed'),
    ...comisiones.map(comisionComoApunte).filter((a) => a.fecha?.startsWith(anio)),
  ].sort((a, b) => {
    if (a.tipo !== b.tipo) return a.tipo === 'ingreso' ? -1 : 1;
    return (a.fecha || '').localeCompare(b.fecha || '');
  });

  const suma = (xs: Apunte[]) => xs.reduce((s, a) => s + importeApunte(a), 0);
  const retenido = (xs: Apunte[]) => xs.reduce((s, a) => s + (a.irpf_amount ?? 0), 0);
  const ingresos = filas.filter((a) => a.tipo === 'ingreso');
  const gastos = filas.filter((a) => a.tipo === 'gasto');

  const trimestres: Trimestre[] = (['T1', 'T2', 'T3', 'T4'] as const).map((t) => {
    const de = (xs: Apunte[]) => xs.filter((a) => a.trimestre === `${anio}-${t}`);
    return { t, ingresos: suma(de(ingresos)), gastos: suma(de(gastos)), retenido: retenido(de(gastos)) };
  });
  trimestres.push({ t: 'TOTAL AÑO', ingresos: suma(ingresos), gastos: suma(gastos), retenido: retenido(gastos) });

  return { filas, trimestres, hayRetenciones: retenido(gastos) > 0 };
}
